import { useEffect, useState } from 'react'
import { IChatDealerBreakdown } from '../models'
import { formatCount, formatDuration, formatPercent } from '../viewModel'
import styles from './ChatbotAnalytics.module.scss'
import { RemoteBadge } from './RemoteBadge'

interface DealerBreakdownPanelProps {
  dealerSapCode: string
  fromUtc: Date
  toUtc: Date
  /** Önbellekte varsa yeniden istek atılmaz. */
  cached?: IChatDealerBreakdown
  loadBreakdown: (dealerSapCode: string, fromUtc: Date, toUtc: Date) => Promise<IChatDealerBreakdown>
  onLoaded: (breakdown: IChatDealerBreakdown) => void
  onShowUsers: (dealerSapCode: string) => void
}

export default function DealerBreakdownPanel({
  dealerSapCode,
  fromUtc,
  toUtc,
  cached,
  loadBreakdown,
  onLoaded,
  onShowUsers,
}: DealerBreakdownPanelProps) {
  const [breakdown, setBreakdown] = useState<IChatDealerBreakdown | null>(cached ?? null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (cached) {
      setBreakdown(cached)
      return
    }

    let active = true
    loadBreakdown(dealerSapCode, fromUtc, toUtc)
      .then(result => {
        if (!active) return
        setBreakdown(result)
        onLoaded(result)
      })
      .catch(() => {
        if (active) setFailed(true)
      })

    return () => {
      active = false
    }
  }, [cached, dealerSapCode, fromUtc, toUtc, loadBreakdown, onLoaded])

  if (failed) {
    return (
      <p role='alert' data-testid={`breakdown-error-${dealerSapCode}`}>
        Bayi detayı yüklenemedi.
      </p>
    )
  }

  if (!breakdown) {
    return (
      <p role='status' data-testid={`breakdown-loading-${dealerSapCode}`}>
        Yükleniyor…
      </p>
    )
  }

  return (
    <div className={styles.breakdownPanel} data-testid={`breakdown-panel-${dealerSapCode}`}>
      {/* YALNIZCA aggregate: intent/tool adı, sayaç ve oran. Konuşma metni hiçbir uçtan gelmez (ADR-017). */}
      {/* Faz-45: kırılım "Deneme Kartı Hariç" uygulamaz; içindeki remote turlar rozetle belirtilir. */}
      {(breakdown.RemoteTurnCount ?? 0) > 0 ? (
        <p data-testid={`breakdown-remote-${dealerSapCode}`}>
          Kırılım, Index çalışanlarının remote sorularını da içerir:
          <RemoteBadge count={breakdown.RemoteTurnCount} />
        </p>
      ) : null}
      <div className={styles.tableGrid}>
        <div className={styles.tableScroll}>
          <table>
            <caption>En çok kullanılan niyetler</caption>
            <thead>
              <tr>
                <th scope='col'>Niyet</th>
                <th scope='col'>Tur</th>
                <th scope='col'>Oran</th>
                <th scope='col'>Ort. süre</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.TopIntents.map(intent => (
                <tr key={intent.Intent}>
                  <th scope='row'>{intent.Intent}</th>
                  <td>{formatCount(intent.Count)}</td>
                  <td>{formatPercent(intent.Rate)}</td>
                  <td>{formatDuration(intent.AverageDurationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.tableScroll}>
          <table>
            <caption>Araç kullanımı</caption>
            <thead>
              <tr>
                <th scope='col'>Araç</th>
                <th scope='col'>Kullanım</th>
                <th scope='col'>Hata</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.TopTools.map(tool => (
                <tr key={tool.Tool}>
                  <th scope='row'>{tool.Tool}</th>
                  <td>{formatCount(tool.Count)}</td>
                  <td>{formatCount(tool.ErrorCount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.tableScroll}>
          <table>
            <caption>Sonuç ve giriş modu</caption>
            <tbody>
              {breakdown.OutcomeBreakdown.concat(breakdown.InputModeBreakdown).map(item => (
                <tr key={item.Key}>
                  <th scope='row'>{item.Key}</th>
                  <td>{formatCount(item.Count)}</td>
                  <td>{formatPercent(item.Rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <button
        type='button'
        data-testid={`breakdown-show-users-${dealerSapCode}`}
        className={styles.secondaryButton}
        onClick={() => onShowUsers(dealerSapCode)}
      >
        Bu bayinin kullanıcıları
      </button>
    </div>
  )
}
